import { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import './App.css';
import CloudPlatforms from './pages/CloudPlatforms/CloudPlatforms';
import ScalableElastic from './pages/ScalableElastic/ScalableElastic';
import SecureReliable from './pages/SecureReliable/SecureReliable';
import CostOptimization from './pages/CostOptimization/CostOptimization';
import CloudServicesPage from './pages/CloudServices/CloudServices';
import BuiltForFuture from './pages/BuiltForFuture/BuiltForFuture';
import WordPressReact from './pages/WebsiteDevelopment/WordPressReact';
import WebsiteMaintenance from './pages/WebsiteDevelopment/WebsiteMaintenance';
import WebsiteBeautification from './pages/WebsiteDevelopment/WebsiteBeautification';
import DeploymentRenewals from './pages/WebsiteDevelopment/DeploymentRenewals';
import SEOOptimization from './pages/WebsiteDevelopment/SEOOptimization';
import WebsiteTransfer from './pages/WebsiteDevelopment/WebsiteTransfer';
import WebApplications from './pages/SaaSDevelopment/WebApplications';
import MobileApps from './pages/SaaSDevelopment/MobileApps';
import UIUXDesign from './pages/SaaSDevelopment/UIUXDesign';
import APIIntegration from './pages/SaaSDevelopment/APIIntegration';
import CustomSoftware from './pages/SaaSDevelopment/CustomSoftware';
import CMSSolutions from './pages/SaaSDevelopment/CMSSolutions';
import SSLCertificates from './pages/SecurityCompliance/SSLCertificates';
import MalwareProtection from './pages/SecurityCompliance/MalwareProtection';
import DataBackup from './pages/SecurityCompliance/DataBackup';
import AccessControl from './pages/SecurityCompliance/AccessControl';
import VulnerabilityScanning from './pages/SecurityCompliance/VulnerabilityScanning';
import './LightThemeOverrides.css';

// Scroll restoration component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ServicePage({ title, eyebrow, description }) {
  return (
    <section className="page">
      <div className="page-content">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  );
}

const cloudSolutions = [
  { path: '/cloud-solutions/cloud-platform', title: 'Cloud Platforms' },
  { path: '/cloud-solutions/scalable-elastic', title: 'Scalable & Elastic' },
  { path: '/cloud-solutions/secure-reliable', title: 'Secure & Reliable' },
  { path: '/cloud-solutions/cost-optimization', title: 'Cost Optimization' },
  { path: '/cloud-solutions/cloud-services', title: 'Cloud Services' },
  { path: '/cloud-solutions/built-for-the-future', title: 'Built for the Future' },
];

const saasSolutions = [
  { path: '/saas/web-apps', title: 'Web Applications' },
  { path: '/saas/mobile-apps', title: 'Mobile Apps' },
  { path: '/saas/ui-ux', title: 'UI/UX Design' },
  { path: '/saas/api-integration', title: 'API Integration' },
  { path: '/saas/custom-software', title: 'Custom Software' },
  { path: '/saas/cms-solutions', title: 'CMS Solutions' },
];

const websiteSolutions = [
  { path: '/website/wordpress-react', title: 'WordPress & React' },
  { path: '/website/maintenance', title: 'Maintenance & Updates' },
  { path: '/website/beautification', title: 'Website Beautification' },
  { path: '/website/deployment-renewal', title: 'Deployment & Renewals' },
  { path: '/website/seo', title: 'SEO Optimization' },
  { path: '/website/transfer-migration', title: 'Website Transfer & Migration' },
];

const securitySolutions = [
  { path: '/security/ssl-certificates', title: 'SSL Certificates' },
  { path: '/security/malware-protection', title: 'Malware Protection' },
  { path: '/security/data-backup', title: 'Data Backup & Recovery' },
  { path: '/security/access-control', title: 'Access Control' },
  { path: '/security/vulnerability-scan', title: 'Vulnerability Scanning' },
];

const homeShowcaseItems = [
  {
    title: 'Cloud Operations',
    image: '/images/cloud-services/managed-ops.jpg',
    alt: 'Managed cloud operations dashboard and team workflow'
  },
  {
    title: 'Scalable Systems',
    image: '/images/scalable-elastic/load-balancing.jpg',
    alt: 'Scalable architecture concept with connected servers'
  },
  {
    title: 'Secure Infrastructure',
    image: '/images/secure-reliable/threat-detection.jpg',
    alt: 'Cybersecurity monitoring interface for threat detection'
  },
  {
    title: 'Website Experience',
    image: '/images/website-beautification/design.jpg',
    alt: 'Modern website UI design workspace'
  }
];

function HomePage() {
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal').forEach((element) => {
        element.classList.add('visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-copy reveal visible">
            <span className="eyebrow">Vanbyte Technologies Pvt Ltd</span>
            <h1>Affordable software and cloud solutions for every business size.</h1>
            <p>
              From startups to established enterprises, we deliver secure, scalable digital solutions with transparent pricing and tailored workflows for small, medium, and large clients.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#services">Explore Services</a>
              <a className="button secondary" href="#contact">Start a Project</a>
            </div>
            <div className="hero-trust-strip">
              <span>Cloud-first delivery</span>
              <span>Flexible pricing</span>
              <span>Security-focused builds</span>
            </div>
          </div>

          <div className="hero-panel reveal visible" style={{ '--reveal-delay': '120ms' }}>
            <div className="panel-card panel-gradient reveal visible" style={{ '--reveal-delay': '180ms' }}>
              <h2>Cloud Migration</h2>
              <p>Move faster with scalable infrastructure, automated deployments, and optimized cloud architecture.</p>
            </div>
            <div className="panel-card panel-glow reveal visible" style={{ '--reveal-delay': '240ms' }}>
              <h2>Custom Software</h2>
              <p>Create elegant, reliable applications tailored to your customers and teams.</p>
            </div>
          </div>
        </div>
      </section>

      <main className="home-main">
        <section className="section section-graphics reveal" id="graphics">
          <div className="graphics-header">
            <span className="eyebrow">Creative Visuals</span>
            <h2>Unique graphics that make your product story memorable.</h2>
          </div>
          <div className="graphics-grid">
            <article className="graphic-card reveal" style={{ '--reveal-delay': '80ms' }}>
              <div className="graphic-orbit">
                <svg viewBox="0 0 220 220" aria-hidden="true">
                  <defs>
                    <linearGradient id="orbitA" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" />
                      <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                  </defs>
                  <circle cx="110" cy="110" r="76" fill="none" stroke="url(#orbitA)" strokeWidth="10" />
                  <circle cx="110" cy="110" r="38" fill="#ffffff" stroke="#bae6fd" strokeWidth="8" />
                  <circle cx="56" cy="58" r="14" fill="#06b6d4" />
                  <circle cx="162" cy="164" r="12" fill="#2dd4bf" />
                </svg>
              </div>
              <h3>Cloud Orbit</h3>
              <p>Architecture visuals that communicate movement, reliability, and modern scale.</p>
            </article>

            <article className="graphic-card reveal" style={{ '--reveal-delay': '150ms' }}>
              <div className="graphic-wave">
                <svg viewBox="0 0 320 180" aria-hidden="true">
                  <defs>
                    <linearGradient id="waveA" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#f97316" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                    <linearGradient id="waveB" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#38bdf8" />
                      <stop offset="100%" stopColor="#6366f1" />
                    </linearGradient>
                  </defs>
                  <path d="M0 90 C45 20, 95 20, 140 90 C185 160, 235 160, 320 84" fill="none" stroke="url(#waveA)" strokeWidth="14" strokeLinecap="round" />
                  <path d="M0 116 C45 50, 95 50, 140 116 C185 176, 235 176, 320 108" fill="none" stroke="url(#waveB)" strokeWidth="12" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Flow Timeline</h3>
              <p>Curved motion graphics for showcasing implementation phases and momentum.</p>
            </article>

            <article className="graphic-card reveal" style={{ '--reveal-delay': '220ms' }}>
              <div className="graphic-grid">
                <svg viewBox="0 0 220 220" aria-hidden="true">
                  <rect x="20" y="24" width="82" height="82" rx="18" fill="#0ea5e9" opacity="0.95" />
                  <rect x="116" y="24" width="82" height="82" rx="18" fill="#14b8a6" opacity="0.92" />
                  <rect x="20" y="116" width="82" height="82" rx="18" fill="#f97316" opacity="0.88" />
                  <rect x="116" y="116" width="82" height="82" rx="18" fill="#6366f1" opacity="0.9" />
                </svg>
              </div>
              <h3>Modular Blocks</h3>
              <p>Color-rich components for product showcases, capability maps, and brand identity.</p>
            </article>
          </div>
        </section>

        <section className="section section-intro reveal" id="about">
          <div className="section-copy">
            <span className="eyebrow">About Vanbyte</span>
            <h2>Delivering next-generation cloud and software experiences for small, medium, and large businesses.</h2>
            <p>
              We partner with clients across every stage of growth, offering purpose-built solutions,
              transparent pricing, and flexible support that fits your budget and goals.
            </p>
          </div>
          <div className="stats-grid">
            <div className="stat-card reveal" style={{ '--reveal-delay': '120ms' }}>
              <strong>Small to Enterprise</strong>
              <span>Solutions designed for every business size</span>
            </div>
            <div className="stat-card reveal" style={{ '--reveal-delay': '180ms' }}>
              <strong>Budget-friendly</strong>
              <span>Affordable packages with clear value</span>
            </div>
            <div className="stat-card reveal" style={{ '--reveal-delay': '240ms' }}>
              <strong>Fast delivery</strong>
              <span>Practical execution with predictable timelines</span>
            </div>
          </div>
        </section>

        <section className="section section-services reveal" id="services">
          <div className="section-header">
            <span className="eyebrow">Our Services</span>
            <h2>From cloud strategy to complete software delivery.</h2>
          </div>
          <div className="services-grid">
            <article className="service-card reveal" style={{ '--reveal-delay': '100ms' }}>
              <img className="service-visual" src="/images/cloud/migration.webp" alt="Cloud migration infrastructure" />
              <span className="service-tag">01</span>
              <h3>Cloud Engineering</h3>
              <p>Design, deploy, and manage cloud platforms that stay resilient under real-world load.</p>
            </article>
            <article className="service-card reveal" style={{ '--reveal-delay': '160ms' }}>
              <img className="service-visual" src="/images/built-for-future/workflows.jpg" alt="Software team planning workflows" />
              <span className="service-tag">02</span>
              <h3>SaaS & Application Development</h3>
              <p>Build intuitive user experiences with scalable backend systems and modern interfaces.</p>
            </article>
            <article className="service-card reveal" style={{ '--reveal-delay': '220ms' }}>
              <img className="service-visual" src="/images/wordpress-react/development.jpg" alt="Website development on desktop screens" />
              <span className="service-tag">03</span>
              <h3>Website Development</h3>
              <p>Modern WordPress and React websites with full support for migration, deployment, renewals, maintenance, and visual beautification.</p>
            </article>
            <article className="service-card reveal" style={{ '--reveal-delay': '280ms' }}>
              <img className="service-visual" src="/images/secure-reliable/zero-trust.jpg" alt="Zero trust security visualization" />
              <span className="service-tag">04</span>
              <h3>Security & Compliance</h3>
              <p>Essential protection including SSL certificates, malware removal, data backups, and vulnerability patching.</p>
            </article>
          </div>
        </section>

        <section className="section section-showcase reveal" id="showcase">
          <div className="showcase-header">
            <span className="eyebrow">Visual Showcase</span>
            <h2>See the quality behind our engineering and design.</h2>
          </div>
          <div className="showcase-grid">
            {homeShowcaseItems.map((item, index) => (
              <figure
                key={item.title}
                className="showcase-tile reveal"
                style={{ '--reveal-delay': `${120 + index * 70}ms` }}
              >
                <img src={item.image} alt={item.alt} loading="lazy" />
                <figcaption>{item.title}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="section section-focus reveal">
          <div className="focus-copy">
            <span className="eyebrow">Why choose us</span>
            <h2>Built to support your product, your cloud, and your budget.</h2>
            <p>
              We deliver high-quality software and cloud solutions with transparent workflows,
              flexible pricing, and practical execution for small, medium, and large clients.
            </p>
          </div>
          <div className="focus-list">
            <div className="reveal" style={{ '--reveal-delay': '100ms' }}>
              <h3>Flexible engagement</h3>
              <p>Choose the right delivery model for your project size and budget.</p>
            </div>
            <div className="reveal" style={{ '--reveal-delay': '180ms' }}>
              <h3>Affordable pricing</h3>
              <p>Smart solutions priced for growth without compromising quality.</p>
            </div>
            <div className="reveal" style={{ '--reveal-delay': '260ms' }}>
              <h3>Scalable support</h3>
              <p>As your business grows, our services adapt with the right level of engineering and operations.</p>
            </div>
          </div>
        </section>

        <section className="section section-contact reveal" id="contact">
          <div className="contact-panel reveal" style={{ '--reveal-delay': '120ms' }}>
            <div>
              <span className="eyebrow">Let’s build together</span>
              <h2>Ready to accelerate your next cloud or software initiative?</h2>
              <p>Reach out to Vanbyte Technologies and let’s discuss a solution tailored to your roadmap.</p>
            </div>
            <a className="button primary" href="mailto:hello@vanbyte.com">Contact Us</a>
          </div>
        </section>
      </main>
    </>
  );
}

function App() {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [cloudSolutionsOpen, setCloudSolutionsOpen] = useState(false);
  const [saasOpen, setSaasOpen] = useState(false);
  const [websiteOpen, setWebsiteOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }

      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
      setScrollProgress(Math.max(0, Math.min(1, progress)));
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setServicesOpen(false);
        setCloudSolutionsOpen(false);
        setSaasOpen(false);
        setWebsiteOpen(false);
        setSecurityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToTopAction = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <div className="scroll-progress" style={{ transform: `scaleX(${scrollProgress})` }} />
        <button 
          className={`scroll-top ${showScrollTop ? 'visible' : ''}`}
          onClick={scrollToTopAction}
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
        <nav className="topnav">
          <Link
            to="/"
            className="brand"
            onClick={() => {
              setServicesOpen(false);
              setCloudSolutionsOpen(false);
              setSaasOpen(false);
              setWebsiteOpen(false);
              setSecurityOpen(false);
            }}
          >
            <img src="/images/header-logo.png" alt="Vanbyte logo" className="brand-logo" />
          </Link>
          <div className="nav-links" ref={navRef}>
            <div className="nav-item">
              <button
                type="button"
                className="nav-parent"
                onClick={() => {
                  setServicesOpen((open) => {
                    const nextOpen = !open;
                    if (!nextOpen) {
                      setCloudSolutionsOpen(false);
                      setSaasOpen(false);
                      setWebsiteOpen(false);
                      setSecurityOpen(false);
                    }
                    return nextOpen;
                  });
                }}
              >
                Services
              </button>
              <div className={`submenu ${servicesOpen ? 'open' : ''}`}>
                <div className="submenu-section">
                  <button
                    type="button"
                    className="submenu-toggle"
                    onClick={() => {
                      setCloudSolutionsOpen((open) => !open);
                      setSaasOpen(false);
                      setWebsiteOpen(false);
                      setSecurityOpen(false);
                    }}
                  >
                    Cloud Solutions
                  </button>
                  <div className={`submenu-nested ${cloudSolutionsOpen ? 'open' : ''}`}>
                    {cloudSolutions.map((solution) => (
                      <Link
                        key={solution.path}
                        to={solution.path}
                        onClick={() => {
                          setCloudSolutionsOpen(false);
                          setServicesOpen(false);
                        }}
                      >
                        {solution.title}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="submenu-section">
                  <button
                    type="button"
                    className="submenu-toggle"
                    onClick={() => {
                      setSaasOpen((open) => !open);
                      setCloudSolutionsOpen(false);
                      setWebsiteOpen(false);
                      setSecurityOpen(false);
                    }}
                  >
                    SaaS & App Development
                  </button>
                  <div className={`submenu-nested ${saasOpen ? 'open' : ''}`}>
                    {saasSolutions.map((solution) => (
                      <Link
                        key={solution.path}
                        to={solution.path}
                        onClick={() => {
                          setSaasOpen(false);
                          setServicesOpen(false);
                        }}
                      >
                        {solution.title}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="submenu-section">
                  <button
                    type="button"
                    className="submenu-toggle"
                    onClick={() => {
                      setWebsiteOpen((open) => !open);
                      setCloudSolutionsOpen(false);
                      setSaasOpen(false);
                      setSecurityOpen(false);
                    }}
                  >
                    Website Development
                  </button>
                  <div className={`submenu-nested ${websiteOpen ? 'open' : ''}`}>
                    {websiteSolutions.map((solution) => (
                      <Link
                        key={solution.path}
                        to={solution.path}
                        onClick={() => {
                          setWebsiteOpen(false);
                          setServicesOpen(false);
                        }}
                      >
                        {solution.title}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="submenu-section">
                  <button
                    type="button"
                    className="submenu-toggle"
                    onClick={() => {
                      setSecurityOpen((open) => !open);
                      setCloudSolutionsOpen(false);
                      setSaasOpen(false);
                      setWebsiteOpen(false);
                    }}
                  >
                    Security & Compliance
                  </button>
                  <div className={`submenu-nested ${securityOpen ? 'open' : ''}`}>
                    {securitySolutions.map((solution) => (
                      <Link
                        key={solution.path}
                        to={solution.path}
                        onClick={() => {
                          setSecurityOpen(false);
                          setServicesOpen(false);
                        }}
                      >
                        {solution.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <a href="/#about" className="nav-parent">About</a>
            <a href="/#contact" className="nav-parent">Contact</a>
          </div>
        </nav>

        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/cloud-solutions/cloud-platform" element={<CloudPlatforms />} />
            <Route path="/cloud-solutions/scalable-elastic" element={<ScalableElastic />} />
            <Route path="/cloud-solutions/secure-reliable" element={<SecureReliable />} />
            <Route path="/cloud-solutions/cost-optimization" element={<CostOptimization />} />
            <Route path="/cloud-solutions/cloud-services" element={<CloudServicesPage />} />
            <Route path="/cloud-solutions/built-for-the-future" element={<BuiltForFuture />} />
            
            <Route path="/saas/web-apps" element={<WebApplications />} />
            <Route path="/saas/mobile-apps" element={<MobileApps />} />
            <Route path="/saas/ui-ux" element={<UIUXDesign />} />
            <Route path="/saas/api-integration" element={<APIIntegration />} />
            <Route path="/saas/custom-software" element={<CustomSoftware />} />
            <Route path="/saas/cms-solutions" element={<CMSSolutions />} />

            <Route path="/website/wordpress-react" element={<WordPressReact />} />
            <Route path="/website/maintenance" element={<WebsiteMaintenance />} />
            <Route path="/website/beautification" element={<WebsiteBeautification />} />
            <Route path="/website/deployment-renewal" element={<DeploymentRenewals />} />
            <Route path="/website/seo" element={<SEOOptimization />} />
            <Route path="/website/transfer-migration" element={<WebsiteTransfer />} />

            <Route path="/security/ssl-certificates" element={<SSLCertificates />} />
            <Route path="/security/malware-protection" element={<MalwareProtection />} />
            <Route path="/security/data-backup" element={<DataBackup />} />
            <Route path="/security/access-control" element={<AccessControl />} />
            <Route path="/security/vulnerability-scan" element={<VulnerabilityScanning />} />

            <Route path="*" element={<HomePage />} />
          </Routes>
        </main>

        <footer className="footer">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/images/header-logo.png" alt="Vanbyte logo" className="footer-logo" />
              <p>Affordable software and cloud solutions for every business size.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Core Services</h4>
                <ul>
                  <li><Link to="/cloud-solutions/cloud-services">Cloud Solutions</Link></li>
                  <li><Link to="/saas/custom-software">SaaS Development</Link></li>
                  <li><Link to="/website/wordpress-react">Web Development</Link></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Security & SEO</h4>
                <ul>
                  <li><Link to="/security/ssl-certificates">SSL & Protection</Link></li>
                  <li><Link to="/security/data-backup">Data Backup</Link></li>
                  <li><Link to="/website/seo">SEO Optimization</Link></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/#about">About Us</a></li>
                  <li><a href="/#contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <ul>
                  <li><a href="mailto:info@vanbyte.in">info@vanbyte.in</a></li>
                  <li>Vadodara, India</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Vanbyte Technologies Pvt Ltd. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
